import isElectron from 'is-electron';
import { isArray } from 'lodash';
import pako from 'pako';

import packageJson from '../../package.json';
import { IMonitorInitSync } from '../types/communicator-types';
import Communicator from './communication-adapter';
import createLogger from './logger';

const logger = createLogger('utils.ts');

export const isEmpty = (obj: any) =>
  Object.entries(obj).length === 0 && obj.constructor === Object;

export const arrayToObjParams = (arr: any) => {
  // this is made a no-op function as part of migration from previous executor to new executor
  return arr;
  // return arr.reduce((prev: any, next: any) => {
  //   prev[next.name] = next.value;
  //   return prev;
  // }, {});
};

export const getStylesObj = (config: {
  color: string;
  label: string;
  valueBackgroundColor: string;
  borderColor: string;
}) => {
  const { color, borderColor, label, valueBackgroundColor } = config || {};
  return {
    ...(!!color && { color }),
    ...(!!valueBackgroundColor && { backgroundColor: valueBackgroundColor }),
    ...(!!borderColor && { border: `solid 1px ${borderColor}` }),
  };
};

export const bfs = (node: any, searchTerm: any, path: any = []) => {
  if (!node.type) {
    // This is a leafnode
    if (node.id === searchTerm) {
      return [...path];
    }
    return null;
  }
  for (let i = 0; i < node.children.length; i++) {
    const n = node.children[i];
    if (n.id === searchTerm) {
      return [...path];
    }
  }
  for (let i = 0; i < node.children.length; i++) {
    const n = node.children[i];
    const result: any = bfs(n, searchTerm, [...path, n.id]);
    if (result) {
      return result;
    }
  }
  return null;
};

export const waitForMonitors = (
  noOfMonitors: Number,
  communicator: Communicator<IMonitorInitSync>
) => {
  return new Promise((resolve) => {
    let recorded = 0;
    communicator.receiveMessage((msg) => {
      if (msg.state === 'ready') {
        recorded++;
      }
      if (noOfMonitors === recorded) {
        resolve(recorded);
      }
    });
  });
};

export const arrayToObjTransform = (
  payload: Array<{ name: string; value: any }>
) => {
  if (!isArray(payload)) {
    return payload;
  }
  return payload.reduce((acc, item) => {
    acc[item.name] = item.value;
    return acc;
  }, {});
};

export const getArgVal = (param: any) => {
  const val = param;
  let value;
  // console.log("arg val ", param, val);
  if (!val) {
    return;
  }
  switch (val.type) {
    case 'string':
      value = val.asString;
      break;
    case 'integer':
    case 'double':
      value = val.asNumber;
      break;
    case 'datetime':
      value = new Date(val.asNumber);
      break;
    case 'boolean':
      value = val.asNumber === 1;
      break;
    case 'array':
      value = val.asArray.map((v: any) => getArgVal(v));
      break;
    case 'object':
      console.log(
        'arg getting as obj',
        val.asObject,
        Object.keys(val.asObject)
      );
      const v = val.asObject;
      value = Object.keys(v).reduce((prev: any, curr: any) => {
        prev[curr] = getArgVal(v[curr]);
        return prev;
      }, {});
      break;
  }
  return value;
};

export const calculateTextPosition = (
  labelPosition: { x: number; y: number },
  labelPositionType: string,
  fontSize: number,
  delta: number = 10
) => {
  switch (labelPositionType) {
    case 'topLeft':
      return {
        posX: labelPosition.x - delta,
        posY: labelPosition.y - fontSize,
        textAnchor: 'end',
      };
    case 'topRight':
      return {
        posX: labelPosition.x + delta,
        posY: labelPosition.y - fontSize,
        textAnchor: 'start',
      };
    case 'centralLeft':
      return {
        posX: labelPosition.x - delta,
        posY: labelPosition.y + fontSize / 4,
        textAnchor: 'end',
      };
    case 'centralRight':
      return {
        posX: labelPosition.x + delta,
        posY: labelPosition.y + fontSize / 4,
        textAnchor: 'start',
      };
    case 'bottomLeft':
      return {
        posX: labelPosition.x - delta,
        posY: labelPosition.y + 2 * fontSize,
        textAnchor: 'end',
      };
    case 'bottomRight':
      return {
        posX: labelPosition.x + delta,
        posY: labelPosition.y + 2 * fontSize,
        textAnchor: 'start',
      };
  }
  return {
    posX: 0,
    posY: 0,
  };
};

export const getDashArray = (lineType: string) => {
  let dashArray = '';
  if (lineType === 'dotted') {
    dashArray += '3';
  } else if (lineType == 'dashed') {
    dashArray += '10';
  } else {
    dashArray += '0';
  }
  return dashArray;
};

export const flattenRoleArrForDisplay = (
  items: any,
  levelDict: any,
  level: number = 1
): Array<any> => {
  let resultArr: Array<any> = [];
  if (!items) {
    return [];
  }
  items.forEach((item, index) => {
    resultArr.push({
      label: item.label,
      console: item.console,
      status: item.status,
      roleStatus: item.roleStatus,
      level: level,
      isSelected: item.isSelected,
      levelDict: {
        ...levelDict,
        [level]: false,
      },
      lastChild: items.length - 1 === index,
    });
    if (item.children) {
      resultArr = resultArr.concat(
        flattenRoleArrForDisplay(
          item.children,
          {
            ...levelDict,
            [level]: items.length - 1 === index,
          },
          level + 1
        )
      );
    }
  });
  return resultArr;
};

export const catiz_menu_bfs = (
  node: any,
  search_term: string,
  path: Array<string> = []
) => {
  if (node.id === search_term) {
    return [...path, node.id];
  }
  if (node.children) {
    for (let i = 0; i < node.children.length; i++) {
      if (node.children[i].id === search_term) {
        return [...path, node.id, node.children[i].id];
      }
    }
    for (let i = 0; i < node.children.length; i++) {
      const result = catiz_menu_bfs(node.children[i], search_term, [
        ...path,
        node.id,
      ]);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export const convertIdToCssSelectorId = (id: string) => {
  return id.replace(/[&\/\\#, +()$~%.’“:*?\[\]<>{}\n\r]/g, '_');
};

export const playerVersion: string = (() => {
  return `P4.${packageJson.version}`;
})();

// export const flattenRoleArrForDisplay = (
//   items: any,
//   level: number = 1
// ): Array<any> => {
//   let resultArr: Array<any> = [];
//   if (!items) {
//     return [];
//   }
//   items.forEach((item) => {
//     resultArr.push({
//       label: item.label,
//       console: item.console,
//       status: item.status,
//       level: level,
//     });
//     if (item.children) {
//       resultArr = resultArr.concat(
//         flattenRoleArrForDisplay(item.children, level + 1)
//       );
//     }
//   });
//   return resultArr;
// };

export const stringifyWithoutCycles = (obj: any) => {
  const cache = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (cache.has(value)) {
        // Circular reference found, discard key
        return '[Circular]';
      }
      // Store value in our set
      cache.add(value);
    }
    return value;
  });
};

export const areSetsEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const openDebugLogger = () => {
  if (isElectron() && window.NotsElectron) {
    console.log("open as electron app's window");
    window.NotsElectron.send('open-debug-logger', {});
  } else {
    console.log("open as browser's window");
    window.open('/debug-logger', '_blank');
  }
};
(window as any).openNOTSDebugLogger = openDebugLogger;

export const stripProtocol = (url: string) => {
  return url.replace(/(^\w+:|^)\/\//, '');
};

export const getElectronConfig = () => {
  if (isElectron()) {
    logger.log('getElectronConfig', { href: window.location.href });

    // get communicationMode, socketServerPort and socketServerIP from location.href
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const communicationMode = urlParams.get('communicationMode');
    const socketServerPort = urlParams.get('socketServerPort');
    const socketServerIP = urlParams.get('socketServerIP');
    const proxyServerPort = urlParams.get('proxyServerPort');
    return {
      communicationMode,
      socketServerPort,
      socketServerIP,
      proxyServerPort,
    };
  }
  return undefined;
};

// compress a string using pako library and gzip
export const compressString = (string: string): Uint8Array => {
  const compressedBuffer = pako.gzip(string, { level: 8 });
  return compressedBuffer;
};

// decompress a string using pako library and gzip
export const decompressString = (compressedBuffer: any): string => {
  const decompressedString = pako.ungzip(compressedBuffer, {
    to: 'string',
  });
  return decompressedString;
};

// convert Uint8Array to Base64 string
export const uint8ArrayToBase64 = (array: Uint8Array) => {
  let binary = '';
  const len = array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(array[i]);
  }
  return btoa(binary);
};

// convert Base64 string to Uint8Array
export const base64ToUint8Array = (base64: string): Uint8Array => {
  const str = atob(base64);
  const array = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    array[i] = str.charCodeAt(i);
  }
  return array;
};

type Func<T, R> = (input: T) => R;
type AnyFunc = Func<any, any>;

function composeFunctions<T1, T2, R>(
  fn1: Func<T1, T2>,
  fn2: Func<T2, R>
): Func<T1, R>;
function composeFunctions<T1, T2, T3, R>(
  fn1: Func<T1, T2>,
  fn2: Func<T2, T3>,
  fn3: Func<T3, R>
): Func<T1, R>;
function composeFunctions<T1, T2, T3, T4, R>(
  fn1: Func<T1, T2>,
  fn2: Func<T2, T3>,
  fn3: Func<T3, T4>,
  fn4: Func<T4, R>
): Func<T1, R>;
function composeFunctions(...fns: AnyFunc[]): AnyFunc {
  return (x: any): any => fns.reduce((v, f) => f(v), x);
}

export { composeFunctions };
